import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MenuBarComponent } from '../../core/components/menu-bar/menu-bar.component';
import { FooterComponent } from '../../core/components/footer/footer.component';
import { NgbModal, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCircleCheck, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { AuthService } from '../../core/services/auth.service';
import { SessionService } from '../../core/services/session.service';
import { Gateway } from '../../core/interfaces/Gateway';
import { ProvisionGatewayModalComponent } from '../../core/components/provision-gateway-modal/provision-gateway-modal.component';
import { GatewayToolModalComponent, GatewayToolMode } from '../../core/components/gateway-tool-modal/gateway-tool-modal.component';
import { AdminNavComponent } from '../../core/components/admin-nav/admin-nav.component';

@Component({
    selector: 'app-gateways',
    imports: [CommonModule, FormsModule, FontAwesomeModule, NgbDropdownModule, MenuBarComponent, FooterComponent, AdminNavComponent],
    templateUrl: './gateways.component.html',
    styleUrl: './gateways.component.scss',
})
export class GatewaysComponent implements OnInit {
    gateways: Gateway[] = [];
    loading = false;

    // Font Awesome icons for the Connected column.
    readonly faCircleCheck = faCircleCheck;
    readonly faCircleExclamation = faCircleExclamation;

    newActivationCode = '';
    activating = false;

    /** Admin table search (matches customer / uid / activate key). */
    search = '';

    /** SPY tool — a POST form submits the gateway's `database` here (new tab). */
    readonly spyUrl = 'http://admin.fyes.gr/spy.php';

    constructor(
        private authService: AuthService,
        private session: SessionService,
        private router: Router,
        private modalService: NgbModal,
    ) { }

    get isAdmin(): boolean {
        return this.session.isAdmin();
    }

    /** Admin table rows filtered by the search box (customer / uid / code). */
    get filteredGateways(): Gateway[] {
        const q = this.search.trim().toLowerCase();
        if (!q) return this.gateways;
        return this.gateways.filter((gw) =>
            (gw.descr || '').toLowerCase().includes(q) ||
            (gw.uid || '').toLowerCase().includes(q) ||
            (gw.code || '').toLowerCase().includes(q),
        );
    }

    /** Format a dash-stripped uid into 8-char groups for display. */
    formatUid(uid: string): string {
        return uid ? (uid.match(/.{1,8}/g) || [uid]).join('-') : '';
    }

    /** Format an ISO activation date as dd-mm-yyyy (dash if empty/invalid). */
    formatDate(value?: string | null): string {
        if (!value) return '—';
        const d = new Date(value);
        if (isNaN(d.getTime())) return value;
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        return `${dd}-${mm}-${d.getFullYear()}`;
    }

    /** Toggle a gateway's active state (admin). Optimistic; reverts on error. */
    toggleActive(gw: Gateway, event: Event): void {
        const input = event.target as HTMLInputElement;
        const newValue = input.checked;
        this.authService.setGatewayActive(gw.uid, newValue).subscribe({
            next: (res) => {
                console.log('[set-active] backend response:', res);
                gw.active = newValue ? 1 : 0;
            },
            error: (err) => {
                console.error('[set-active] backend error:', err);
                input.checked = !newValue; // revert the UI
            },
        });
    }

    /** Tools → Empty: cloud unbind + clears activation date (re-activatable). */
    empty(gw: Gateway): void {
        if (!confirm(`Empty "${gw.descr}"? This unbinds the gateway from its user.`)) return;
        this.authService.emptyGateway(gw.uid).subscribe({
            next: () => this.loadGateways(),
            error: (err) => console.error('[empty] error:', err),
        });
    }

    /** Tools → Delete: cloud unbind + retires the code (not re-activatable). */
    deletePermanent(gw: Gateway): void {
        if (!confirm(`Delete "${gw.descr}"? This unbinds it and retires its activation code.`)) return;
        this.authService.deleteGateway(gw.uid).subscribe({
            next: () => this.loadGateways(),
            error: (err) => console.error('[delete] error:', err),
        });
    }

    /** Tools → Change username / Change password / Replace (modal actions). */
    openTool(mode: GatewayToolMode, gw: Gateway): void {
        const ref = this.modalService.open(GatewayToolModalComponent, {
            centered: true,
            windowClass: 'auth-modal-window',
        });
        ref.componentInstance.mode = mode;
        ref.componentInstance.gateway = gw;
        ref.componentInstance.gateways = this.gateways;
        ref.result.then(() => this.loadGateways(), () => { });
    }

    openProvisionModal(): void {
        const ref = this.modalService.open(ProvisionGatewayModalComponent, {
            centered: true,
            windowClass: 'auth-modal-window',
        });
        // Reload the list after a successful provision (Done).
        ref.result.then(() => this.loadGateways(), () => { });
    }

    ngOnInit(): void {
        if (!this.session.isLoggedIn()) {
            // Not logged in — nothing to show here.
            this.router.navigate(['/']);
            return;
        }
        this.loadGateways();
    }

    loadGateways(): void {
        this.loading = true;
        // Admins see every gateway (gw_list with id_user = -1); regular users
        // only their own.
        const request$ = this.isAdmin
            ? this.authService.getAllGateways()
            : this.authService.getGateways();
        request$.subscribe({
            next: (res) => {
                this.gateways = res?.gateways ?? [];
                this.loading = false;
            },
            error: () => {
                this.gateways = [];
                this.loading = false;
            },
        });
    }

    /** Redeem an activation code to bind another gateway to this user. */
    activate(): void {
        const code = this.newActivationCode.trim();
        if (!code || this.activating) return;

        this.activating = true;
        this.authService.activateGateway(code).subscribe({
            next: (res) => {
                console.log('[activate] backend response:', res);
                this.activating = false;
                this.newActivationCode = '';
                this.loadGateways(); // refresh so the new gateway appears
            },
            error: (err) => {
                console.error('[activate] backend error:', err);
                this.activating = false;
            },
        });
    }

}
